import React, { useEffect, useState, useMemo } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Button,
  Input,
  Label,
  Spinner,
  CardFooter,
  Table,
} from "reactstrap";
import Select from "react-select";
import {
  useCreateWorkflowMutation,
  useGetRolesQuery,
  useGetWorkflowByIdQuery,
  useUpdateWorkflowMutation,
  useGetcategoryQuery
} from "slices/workflow/WorkFlowSlice";
import { toast } from "react-toastify";

interface RoleOption {
  value: number;
  label: string;
}

interface CategoryOption {
  value: string;
  label: string;
}

interface Step {
  id: number;
  stepName: string;
  selectedRoles: RoleOption[];
  isRequired: boolean;
}

interface WorkFlowProps {
  setShowAddComponent: (show: boolean) => void;
  workflowId?: string | null;
  categoryKey?: string; // Add category key prop
}

const WorkFlowAdd: React.FC<WorkFlowProps> = ({
  setShowAddComponent,
  workflowId,
  categoryKey,
}) => {
  const [workFlowData, setWorkFlowData] = useState<{ 
    workflow_name: string;
    category_name: string | null;
  }>({
    workflow_name: "",
    category_name: null,
  });
  const [steps, setSteps] = useState<Step[]>([
    { id: 1, stepName: "", selectedRoles: [], isRequired: false },
  ]);
  const [isDirty, setIsDirty] = useState(false);

  const { data: getRoleInfo, isLoading: loadingRoleInfo } =
    useGetRolesQuery(undefined);
  const { data: categoryData, isLoading: loadingCategories } = 
    useGetcategoryQuery(undefined);
  const [postWorkFlowFn, { isLoading: workFlowLoading }] =
    useCreateWorkflowMutation();
  const [patchWorkFlowFn, { isLoading: loadingpatchWorkFlowFn }] =
    useUpdateWorkflowMutation();
  const {
    data: workFlowDetailsById,
    refetch: workFlowRefetchById,
    isLoading: loadingWorkFlowFn,
  } = useGetWorkflowByIdQuery(workflowId!, {
    skip: !workflowId,
  });

  // Prepare category options for dropdown
  const categoryOptions: CategoryOption[] = categoryData
    ? categoryData.map((category: any) => ({
        value: category.key,
        label: category.workflow_category,
      }))
    : [];

  // Find the category option based on the categoryKey
  const selectedCategoryByKey = useMemo(() => {
    if (categoryKey && categoryData) {
      const category = categoryData.find((cat: any) => cat.key === categoryKey);
      return category ? { value: category.key, label: category.workflow_category } : null;
    }
    return null;
  }, [categoryKey, categoryData]);

  // Get the selected category option for dropdown
  const selectedCategoryOption = useMemo(() => {
    // If editing and workflow has category, use that
    if (workFlowDetailsById?.category_name) {
      return categoryOptions.find(option => option.value === workFlowDetailsById.category_name);
    }
    // If creating and category_name is set in state, use that
    if (workFlowData.category_name) {
      return categoryOptions.find(option => option.value === workFlowData.category_name);
    }
    return null;
  }, [workFlowDetailsById, categoryOptions, workFlowData.category_name]);

  // Set the category when component mounts or when categoryKey changes (only for new workflows)
  useEffect(() => {
    if (categoryKey && selectedCategoryByKey && !workflowId) {
      // Only set for new workflows, not when editing
      setWorkFlowData(prev => ({
        ...prev,
        category_name: selectedCategoryByKey.value
      }));
    }
  }, [categoryKey, selectedCategoryByKey, workflowId]);

  useEffect(() => {
    if (workflowId) {
      workFlowRefetchById();
    }
  }, [workflowId, workFlowRefetchById]);

  useEffect(() => {
    if (workFlowDetailsById) {
      setWorkFlowData({ 
        workflow_name: workFlowDetailsById.workflow_name,
        category_name: workFlowDetailsById.category_name || null 
      });
      setSteps(
        workFlowDetailsById.approvals
          ? workFlowDetailsById.approvals.map(
              (approval: any, index: number) => ({
                id: index + 1,
                stepName: approval.step_name,
                selectedRoles: approval.role
                  .map((roleId: number) => {
                    const role = getRoleInfo?.find(
                      (role: any) => role.id === roleId,
                    );
                    return role ? { value: role.id, label: role.name } : null;
                  })
                  .filter(
                    (role: RoleOption | null) => role !== null,
                  ) as RoleOption[],
                isRequired: approval.is_required,
              }),
            )
          : [{ id: 1, stepName: "", selectedRoles: [], isRequired: false }],
      );
    }
  }, [workFlowDetailsById, getRoleInfo]);

  const addStep = () => {
    const newStep: Step = {
      id: steps.length + 1,
      stepName: "",
      selectedRoles: [],
      isRequired: false,
    };
    setSteps([...steps, newStep]);
    setIsDirty(true);
  };

  const deleteStep = (index: number) => {
    const updatedSteps = steps.filter((_, i) => i !== index);
    setSteps(updatedSteps);
    setIsDirty(true);
  };

  const prepareWorkflowData = () => {
    const approvals = steps.map((step, index) => ({
      level: index + 1,
      step_name: step.stepName,
      role: step.selectedRoles.map((role) => role.value),
      is_required: true,
    }));
    return {
      workflow_name: workFlowData.workflow_name,
      category_name: workFlowData.category_name,
      approvals: approvals,
    };
  };

  const saveWorkflow = (e: any) => {
    e.preventDefault();
    const workflowData = prepareWorkflowData();
    if (workflowId) {
      patchWorkFlowFn({ id: workflowId, ...workflowData })
        .unwrap()
        .then(() => {
          setIsDirty(false);
          setWorkFlowData({ 
            workflow_name: "",
            category_name: null 
          });
          setSteps([
            { id: 1, stepName: "", selectedRoles: [], isRequired: false },
          ]);
          setShowAddComponent(false);
          toast.success("Workflow Updated");
        });
    } else {
      postWorkFlowFn(workflowData)
        .unwrap()
        .then(() => {
          setIsDirty(false);
          setWorkFlowData({ 
            workflow_name: "",
            category_name: null 
          });
          setSteps([
            { id: 1, stepName: "", selectedRoles: [], isRequired: false },
          ]);
          setShowAddComponent(false);
          toast.success("Workflow Added");
        });
    }
  };

  const handleStepChange = (index: number, field: keyof Step, value: any) => {
    setSteps((prevSteps) =>
      prevSteps.map((step, i) =>
        i === index ? { ...step, [field]: value } : step,
      ),
    );
    setIsDirty(true);
  };

  const handleMultiSelectChange = (
    index: number,
    selectedOptions: RoleOption[],
  ) => {
    setSteps((prevSteps) =>
      prevSteps.map((step, i) =>
        i === index ? { ...step, selectedRoles: selectedOptions } : step,
      ),
    );
    setIsDirty(true);
  };

  const handleCategoryChange = (selectedOption: CategoryOption | null) => {
    setWorkFlowData(prev => ({
      ...prev,
      category_name: selectedOption ? selectedOption.value : null
    }));
    setIsDirty(true);
  };

  // Determine if category should be disabled
  const isCategoryDisabled = useMemo(() => {
    // Only disable if we're creating a new workflow AND categoryKey is provided
    return !workflowId && !!categoryKey;
  }, [workflowId, categoryKey]);

  return (
    <Card>
      <form onSubmit={(e) => saveWorkflow(e)}>
        <CardHeader>
          <div
            className="d-flex align-items-center"
            style={{ display: "flex", justifyContent: "space-between" }}
          >
            <div className="d-flex gap-3">
              <div>
                <Label for="workflowName">Workflow Name</Label>
                <Input
                  id="workflowName"
                  value={workFlowData.workflow_name}
                  required
                  onChange={(e) =>
                    setWorkFlowData(prev => ({ 
                      ...prev, 
                      workflow_name: e.target.value 
                    }))
                  }
                />
              </div>
              <div>
                <Label for="category">Category</Label>
                <Select
                  id="category"
                  value={selectedCategoryOption}
                  onChange={handleCategoryChange}
                  options={categoryOptions}
                  isLoading={loadingCategories}
                  isClearable
                  required
                  placeholder="Select Category"
                  isDisabled={isCategoryDisabled || loadingCategories}
                />
                {isCategoryDisabled && (
                  <small className="text-muted">
                    Category is pre-selected based on the workflow type
                  </small>
                )}
              </div>
            </div>
            &nbsp;&nbsp;&nbsp;
            <div>
              <Button
                className="btn btn-primary btn-md shadow-none mt-4"
                onClick={addStep}
              >
                Add Step
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardBody>
          <Table>
            <thead>
              <tr>
                <th>Level</th>
                <th>Step Name</th>
                <th>Roles</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {steps.map((step, index) => (
                <tr key={step.id} className="mb-3">
                  <td width={200}>Level {index + 1} Approval</td>
                  <td width={200}>
                    <Input
                      id={`stepName-${index}`}
                      required
                      value={step.stepName}
                      onChange={(e) =>
                        handleStepChange(index, "stepName", e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <Select
                      id={`stepRoles-${index}`}
                      required
                      isMulti
                      value={step.selectedRoles}
                      onChange={(selectedOptions: any) =>
                        handleMultiSelectChange(
                          index,
                          selectedOptions as RoleOption[],
                        )
                      }
                      options={getRoleInfo?.map((role: any) => ({
                        value: role.id,
                        label: role.name,
                      }))}
                      closeMenuOnSelect={false}
                    />
                  </td>
                  <td>
                    <Button
                      className="text-white d-inline-block btn-sm"
                      onClick={() => deleteStep(index)}
                      type="button" // Changed from "submit" to "button"
                      style={{ marginTop: "3px" }}
                    >
                      <i className="ri-delete-bin-5-fill fs-16"></i>{" "}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </CardBody>
        <CardFooter className="d-flex align-items-center">
          <Button
            color="primary"
            disabled={workFlowLoading || loadingpatchWorkFlowFn}
            type="submit"
          >
            {" "}
            {loadingpatchWorkFlowFn && <Spinner size="sm" className="me-2" />}
            {workflowId ? "Update" : "Save"}
          </Button>
          &nbsp;
          <Button
            color="danger"
            disabled={workFlowLoading || loadingpatchWorkFlowFn}
            onClick={() => setShowAddComponent(false)}
            type="button"
          >
            Cancel
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
};

export default WorkFlowAdd;